import React from "react";
import {
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Tooltip,
} from "@mui/material";
import {
  Security,
  Description,
  Download,
  Lock,
  CloudDownload,
  Apps,
  UsbOff,
  VpnKey,
  PhishingOutlined,
  UpdateOutlined,
  BackupOutlined,
  ScreenLockPortrait,
  NetworkWifi,
  Policy,
  BugReport,
  VerifiedUser,
  Security as SecurityIcon,
  Link,
  Info,
} from "@mui/icons-material";

import "../../styles/pages/cybersecurity.scss";

const CybersecurityPage = () => {
  const cybersecurityTips = [
    // Critical Security Practices
    {
      title: "Password Security",
      content: (
        <div className="tip-content-wrapper">
          Use strong, unique passwords for all accounts and never share them
          with anyone. Change your password regularly and use different
          passwords for different accounts.
          <Tooltip
            title={
              <div style={{ padding: "8px" }}>
                <Typography variant="body2" component="div">
                  <strong>Strong Password Requirements:</strong>
                  <ul style={{ marginTop: "4px", paddingLeft: "16px" }}>
                    <li>Be at least 14 characters long (and ideally longer)</li>
                    <li>
                      Be a mix of uppercase and lowercase characters plus
                      symbols and letters
                    </li>
                    <li>
                      Avoid common and obvious sequences: dictionary words
                      (12345, password123, food) and personal information
                      (birthdate, pet names)
                    </li>
                  </ul>
                </Typography>
              </div>
            }
            arrow
            placement="bottom"
          >
            <Info
              fontSize="small"
              style={{
                marginLeft: "8px",
                cursor: "pointer",
                verticalAlign: "middle",
                color: "#1976d2",
              }}
            />
          </Tooltip>
        </div>
      ),
      icon: <VpnKey />,
      priority: "critical",
    },
    {
      title: "Two-Factor Authentication",
      content:
        "Enable two-factor authentication where available. This adds an extra layer of security beyond just passwords.",
      icon: <VerifiedUser />,
      priority: "critical",
    },
    {
      title: "Phishing Awareness",
      content:
        "Be vigilant about phishing attempts. Never click on suspicious links or download attachments from unknown sources. Verify sender identities.",
      icon: <PhishingOutlined />,
      priority: "critical",
    },
    {
      title: "System Updates",
      content:
        "Keep your devices(phones, laptops, etc.), installed software, and systems up to date. Security updates patch vulnerabilities that could be exploited by attackers.",
      icon: <UpdateOutlined />,
      priority: "high",
    },
    // High Impact Practices
    {
      title: "Data Backup",
      content:
        "Regularly backup important data to secure locations. This protects against data loss from ransomware or system failures.",
      icon: <BackupOutlined />,
      priority: "high",
    },
    {
      title: "Screen Lock",
      content:
        "Always lock your screen when stepping away from your computer. This prevents unauthorized access to your work.",
      icon: <ScreenLockPortrait />,
      priority: "important",
    },
    {
      title: "Safe Downloads",
      content:
        "Only download files from trusted sources. Be cautious with email attachments and links from unknown senders.",
      icon: <CloudDownload />,
      priority: "high",
    },
    // Important Practices
    {
      title: "Network Security",
      content:
        "Use secure networks for sensitive work. Avoid using public Wi-Fi for accessing confidential information.",
      icon: <NetworkWifi />,
      priority: "important",
    },
    {
      title: "Approved Applications",
      content: (
        <span>
          Only use applications that have been approved/endorsed by ICTDO. You
          may approach them for inquiries via{" "}
          <a
            href="mailto:techsupport@upou.edu.ph"
            style={{ color: "inherit", fontWeight: 600 }}
          >
            techsupport@upou.edu.ph
          </a>
          . Unauthorized software may pose security risks.
        </span>
      ),
      icon: <Apps />,
      priority: "important",
    },
    {
      title: "USB Drive Safety",
      content:
        "Never insert unknown USB drives into your computer. They may contain malware that can infect your system.",
      icon: <UsbOff />,
      priority: "important",
    },
    {
      title: "Incident Reporting",
      content: (
        <span>
          Report any security incidents or suspicious activities immediately to
          ICTDO via{" "}
          <a
            href="mailto:techsupport@upou.edu.ph"
            style={{ color: "inherit", fontWeight: 600 }}
          >
            techsupport@upou.edu.ph
          </a>
        </span>
      ),
      icon: <BugReport />,
      priority: "critical",
    },
    {
      title: "UP Acceptable Use Policy",
      content: (
        <span>
          Follow UP's acceptable use policies and guidelines. Visit{" "}
          <a
            href="https://upd.edu.ph/aup/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "inherit", fontWeight: 600 }}
          >
            https://upd.edu.ph/aup/
          </a>{" "}
          for more information.
        </span>
      ),
      icon: <Link />,
      priority: "high",
    },
  ];

  const handleDownloadTips = () => {
    const pdfUrl =
      process.env.PUBLIC_URL + "/assets/UPOU-Cyber-Hygiene-Essentials.pdf";

    // Create a temporary link element
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "UPOU-Cyber-Hygiene-Essentials.pdf"; // Set the download filename
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Group tips by priority
  const groupedTips = {
    critical: cybersecurityTips.filter((tip) => tip.priority === "critical"),
    high: cybersecurityTips.filter((tip) => tip.priority === "high"),
    important: cybersecurityTips.filter((tip) => tip.priority === "important"),
  };

  return (
    <div className="cybersecurity-page">
      <div className="page-header">
        <Security className="header-icon" />
        <Typography variant="h4">Cybersecurity Guide</Typography>
      </div>

      <div className="cybersecurity-content">
        <Card className="pdf-card">
          <CardContent>
            <div className="pdf-icon">
              <Description />
            </div>
            <Typography variant="h6" className="pdf-title">
              Quick Tips for Cybersecurity
            </Typography>
            <Typography variant="body2" className="pdf-description">
              Essential guidelines and best practices for maintaining security
              in your daily work
            </Typography>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={handleDownloadTips}
              className="download-button"
            >
              Download PDF
            </Button>
          </CardContent>
        </Card>

        <div className="content-sections">
          <div className="security-tips">
            <Typography variant="h5" className="section-title">
              Essential Tips by Priority
            </Typography>
            {Object.entries(groupedTips).map(([priority, tips]) => (
              <div key={priority} className="priority-section">
                <Typography variant="h6" className="priority-title">
                  {priority.charAt(0).toUpperCase() + priority.slice(1)}{" "}
                  Priority
                </Typography>
                <div className="tips-grid">
                  {tips.map((tip, index) => (
                    <Card key={index} className={`tip-card ${priority}`}>
                      <CardContent>
                        <div className="tip-icon">{tip.icon}</div>
                        <Typography variant="h6" className="tip-title">
                          {tip.title}
                        </Typography>
                        <Typography variant="body2" className="tip-content">
                          {tip.content}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CybersecurityPage;
